import { client } from '.';

const DEVICE_KEY = 'bandido:push-device-id';
const CLEANUP_TIMEOUT_MS = 1500;
const PRESENCE_INTERVAL_MS = 25_000;
const ACTIVE_WINDOW_MS = 60_000;
let pushSession = 0;
let cleanupGeneration = 0;
let registrationQueue: Promise<unknown> = Promise.resolve();
let presenceTabId: string | undefined;
let presenceSequence = 0;

const bounded = async (operation: Promise<unknown>) => {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      operation,
      new Promise<void>((resolve) => {
        timeout = setTimeout(resolve, CLEANUP_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeout) clearTimeout(timeout);
  }
};

const applicationServerKey = (value: string) => {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(window.atob(base64), (character) => character.charCodeAt(0));
};

const keysMatch = (existing: ArrayBuffer | null, current: Uint8Array) => {
  if (!existing) return false;
  const bytes = new Uint8Array(existing);
  return bytes.length === current.length && bytes.every((value, index) => value === current[index]);
};

const deviceId = () => {
  const current = window.localStorage.getItem(DEVICE_KEY);
  if (current) return current;
  const created = window.crypto.randomUUID();
  window.localStorage.setItem(DEVICE_KEY, created);
  return created;
};

export const getStoredPushDeviceId = () => window.localStorage.getItem(DEVICE_KEY);

export const inferPushDeviceLabel = () => {
  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';
  if (/iPhone/i.test(userAgent)) return 'iPhone';
  if (/iPad|iPod/i.test(userAgent) || (/Mac/i.test(platform) && navigator.maxTouchPoints > 1)) return 'iPad';
  if (/Android/i.test(userAgent)) return 'Android';
  if (/Windows/i.test(userAgent) || /Win/i.test(platform)) return 'Windows';
  if (/Mac/i.test(userAgent) || /Mac/i.test(platform)) return 'Mac';
  if (/Linux/i.test(userAgent) || /Linux/i.test(platform)) return 'Linux';
  return 'Navegador';
};

export const createPresenceTabIdentity = () => window.crypto.randomUUID();

const tabId = () => {
  presenceTabId ??= createPresenceTabIdentity();
  return presenceTabId;
};

export const presenceIsActive = (visible: boolean, focused: boolean, lastActivityAt: number | undefined, now: number) =>
  lastActivityAt !== undefined && visible && focused && now - lastActivityAt <= ACTIVE_WINDOW_MS;

export function startPushPresence(agencyId: number) {
  const registeredDeviceId = deviceId();
  const registeredTabId = tabId();
  let lastActivityAt: number | undefined;
  let lastSentActive: boolean | undefined;

  const send = async (force = false) => {
    const active = presenceIsActive(
      document.visibilityState === 'visible',
      document.hasFocus(),
      lastActivityAt,
      Date.now(),
    );
    if (!force && !active && lastSentActive === false) return;
    lastSentActive = active;
    await client.post('notifications/push/presence', {
      deviceId: registeredDeviceId,
      tabId: registeredTabId,
      agencyId,
      active,
      sequence: ++presenceSequence,
    });
  };
  const activity = () => {
    const now = Date.now();
    const wasActive = presenceIsActive(
      document.visibilityState === 'visible',
      document.hasFocus(),
      lastActivityAt,
      now,
    );
    lastActivityAt = now;
    if (!wasActive && document.visibilityState === 'visible' && document.hasFocus()) {
      void send(true).catch(() => undefined);
    }
  };
  const stateChanged = () => void send(true).catch(() => undefined);
  const activityEvents: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'touchstart'];
  activityEvents.forEach((event) => window.addEventListener(event, activity, { passive: true }));
  document.addEventListener('visibilitychange', stateChanged);
  window.addEventListener('focus', stateChanged);
  window.addEventListener('blur', stateChanged);
  const interval = window.setInterval(() => void send().catch(() => undefined), PRESENCE_INTERVAL_MS);
  void send(true).catch(() => undefined);

  return () => {
    window.clearInterval(interval);
    activityEvents.forEach((event) => window.removeEventListener(event, activity));
    document.removeEventListener('visibilitychange', stateChanged);
    window.removeEventListener('focus', stateChanged);
    window.removeEventListener('blur', stateChanged);
    void client
      .post('notifications/push/presence', {
        deviceId: registeredDeviceId,
        tabId: registeredTabId,
        agencyId,
        active: false,
        sequence: ++presenceSequence,
      })
      .catch(() => undefined);
  };
}

const registerPush = async (agencyId: number, session: number) => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  const { data: key } = await client.get<{ enabled: boolean; publicKey: string | null }>('notifications/push/key');
  if (!key.enabled || !key.publicKey) return false;
  const currentKey = applicationServerKey(key.publicKey);
  const registration = await navigator.serviceWorker.ready;
  if (session !== pushSession) return false;
  const existing = await registration.pushManager.getSubscription();
  if (existing && !keysMatch(existing.options.applicationServerKey, currentKey)) {
    if (session !== pushSession) return false;
    await existing.unsubscribe();
    if (session !== pushSession) return false;
  }
  const subscription =
    (existing && keysMatch(existing.options.applicationServerKey, currentKey) ? existing : null) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: currentKey,
    }));
  const json = subscription.toJSON();
  if (session !== pushSession) return false;
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false;
  const registeredDeviceId = deviceId();
  await client.post('notifications/push/subscriptions', {
    deviceId: registeredDeviceId,
    label: inferPushDeviceLabel(),
    agencyId,
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
    expirationTime: json.expirationTime,
  });
  if (session !== pushSession) {
    await client.delete(`notifications/push/subscriptions/${registeredDeviceId}`).catch(() => undefined);
    return false;
  }
  return true;
};

export function subscribeToPush(agencyId: number) {
  const session = pushSession;
  ++cleanupGeneration;
  const registration = registrationQueue.then(() => registerPush(agencyId, session));
  registrationQueue = registration.then(
    () => undefined,
    () => undefined,
  );
  return registration;
}

export async function revokePushSubscription() {
  const cleanup = ++cleanupGeneration;
  ++pushSession;
  const id = window.localStorage.getItem(DEVICE_KEY);
  if (!id) return;
  try {
    await bounded(client.delete(`notifications/push/subscriptions/${id}`).catch(() => undefined));
  } finally {
    if ('serviceWorker' in navigator) {
      const currentSubscription = (registration?: ServiceWorkerRegistration) =>
        cleanup === cleanupGeneration ? registration?.pushManager.getSubscription() : undefined;
      await bounded(
        navigator.serviceWorker
          .getRegistration()
          .then(currentSubscription)
          .then((subscription) => (cleanup === cleanupGeneration ? subscription?.unsubscribe() : undefined))
          .catch(() => undefined),
      );
    }
    window.localStorage.removeItem(DEVICE_KEY);
  }
}
